import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    VStack,
    HStack,
    Text,
    Badge,
    useToast,
    Input,
    Select,
    IconButton,
    Tooltip,
    Spinner,
    Alert,
    AlertIcon,
    Flex,
    useColorModeValue,
    Avatar,
    InputGroup,
    InputLeftElement,
    useDisclosure,
    Grid,
} from '@chakra-ui/react';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import CustomPagination from 'components/pagination/Pagination';
import {
    RefreshCw,
    UserCheck,
    UserX,
    UserPlus,
    Search,
    Plus,
    Users
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { trainerService, GetTrainersParams } from '../../../../services/trainerService';
import {
    agencyRelationshipService,
    GetAgencyRelationsResult,
    TrainerWithClientsData
} from '../../../../services/agencyRelationshipService';
import { TrainerProfile } from '../../../../generated/fitness-service/v1/common_pb';
import CreateTrainerModal from './CreateTrainerModal';
import GenerateSignupLinkModal from './GenerateSignupLinkModal';
import ViewTrainerClientsModal from './ViewTrainerClientsModal';

const columnHelper = createColumnHelper<TrainerProfile.AsObject>();

const TrainersTable: React.FC = () => {
    const [trainers, setTrainers] = useState<TrainerProfile.AsObject[]>([]);
    const [trainerRelations, setTrainerRelations] = useState<GetAgencyRelationsResult | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState<string>('all');
    const [pageIndex, setPageIndex] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize] = useState(10);
    const [selectedTrainerForModal, setSelectedTrainerForModal] = useState<TrainerWithClientsData | null>(null);
    const toast = useToast();
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isClientsModalOpen,
        onOpen: onClientsModalOpen,
        onClose: onClientsModalClose
    } = useDisclosure();
    const {
        isOpen: isSignupLinkModalOpen,
        onOpen: onSignupLinkModalOpen,
        onClose: onSignupLinkModalClose
    } = useDisclosure();

    const bgColor = useColorModeValue('white', 'navy.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const textColor = useColorModeValue('navy.700', 'white');
    const textColorSecondary = useColorModeValue('gray.600', 'gray.400');

    const fetchTrainers = async (params: GetTrainersParams = {}) => {
        setIsLoading(true);
        try {
            // Fetch trainers
            const response = await trainerService.getTrainers({
                page: pageIndex + 1, // Convert 0-based to 1-based
                limit: pageSize,
                activeOnly: activeFilter === 'active',
                ...params,
            });

            setTrainers(response.trainersList || []);
            setTotalCount(response.totalCount || 0);

            // Fetch trainer-client relationships for agency view
            const relationsResponse = await agencyRelationshipService.getAgencyTrainerClientRelations({
                page: 1,
                limit: 100, // Get more relationships to show clients
                activeOnly: false,
            });

            setTrainerRelations(relationsResponse);

        } catch (error: any) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to fetch trainers',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewClientCalendar = (clientId: string, clientName: string, trainerId: string, trainerName: string) => {
        // Navigate to the calendar page with client information
        navigate(`/agency/calendar/${clientId}?client_name=${encodeURIComponent(clientName)}&trainer_name=${encodeURIComponent(trainerName)}&trainer_id=${trainerId}`);
    };

    const getTrainerClients = (trainerId: string) => {
        if (!trainerRelations) return [];
        const trainerRel = trainerRelations.trainerRelations.find(tr => tr.trainer.id === trainerId);
        return trainerRel ? trainerRel.clientRelations : [];
    };

    const getTrainerWithClients = (trainerId: string): TrainerWithClientsData | null => {
        if (!trainerRelations) return null;
        return trainerRelations.trainerRelations.find(tr => tr.trainer.id === trainerId) || null;
    };

    const handleViewTrainerClients = (trainerId: string) => {
        const trainerWithClients = getTrainerWithClients(trainerId);
        if (trainerWithClients) {
            setSelectedTrainerForModal(trainerWithClients);
            onClientsModalOpen();
        } else {
            toast({
                title: 'Error',
                description: 'Trainer data not found',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        fetchTrainers();
    }, [pageIndex, activeFilter]);

    const handleStatusToggle = async (trainerId: string, isActive: boolean) => {
        try {
            if (isActive) {
                await trainerService.deactivateTrainer(trainerId);
                toast({
                    title: 'Trainer Deactivated',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                await trainerService.activateTrainer(trainerId);
                toast({
                    title: 'Trainer Activated',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            }
            fetchTrainers();
        } catch (error: any) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to update trainer status',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const filteredTrainers = trainers.filter(trainer =>
        trainer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trainer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trainer.specialization.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // React Table configuration
    const columns = [
        columnHelper.accessor(row => row.name, {
            id: 'name',
            header: () => (
                <Text
                    fontSize={{ sm: '10px', lg: '12px' }}
                    color="gray.400"
                >
                    Name
                </Text>
            ),
            cell: (info) => (
                <HStack spacing={3}>
                    <Avatar 
                        name={info.row.original.name} 
                        size="sm"
                        bg={info.row.original.isActive ? 'blue.500' : 'gray.400'}
                    />
                    <Text fontWeight="500" color={textColor}>
                        {info.getValue()}
                    </Text>
                </HStack>
            ),
        }),
        columnHelper.accessor(row => row.trainerCode, {
            id: 'code',
            header: () => (
                <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
                    Code
                </Text>
            ),
            cell: (info) => (
                <Badge colorScheme="purple" variant="solid" fontSize="xs">
                    {info.getValue()}
                </Badge>
            ),
        }),
        columnHelper.accessor(row => row.email, {
            id: 'email',
            header: () => (
                <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
                    Email
                </Text>
            ),
            cell: (info) => (
                <Text fontSize="sm" color={textColor} fontWeight="400">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor(row => row.phone, {
            id: 'phone',
            header: () => (
                <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
                    Phone
                </Text>
            ),
            cell: (info) => (
                <Text fontSize="sm" color={textColor} fontWeight="400">
                    {info.getValue() || (
                        <Text as="span" color={textColorSecondary} fontStyle="italic">
                            Not provided
                        </Text>
                    )}
                </Text>
            ),
        }),
        columnHelper.accessor(row => row.specialization, {
            id: 'specialization',
            header: () => (
                <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
                    Specialization
                </Text>
            ),
            cell: (info) => (
                <Text fontSize="sm" color={textColor} fontWeight="400">
                    {info.getValue() || (
                        <Text as="span" color={textColorSecondary} fontStyle="italic">
                            General Fitness
                        </Text>
                    )}
                </Text>
            ),
        }),
        columnHelper.accessor(row => row.experienceYears, {
            id: 'experience',
            header: () => (
                <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
                    Experience
                </Text>
            ),
            cell: (info) => (
                <Badge colorScheme="green" variant="outline" fontSize="xs">
                    {info.getValue()} years
                </Badge>
            ),
        }),
        columnHelper.accessor(row => row.id, {
            id: 'clients',
            header: () => (
                <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
                    Clients
                </Text>
            ),
            cell: (info) => {
                const trainerClients = getTrainerClients(info.getValue());
                return (
                    <HStack spacing={2}>
                        <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                            {trainerClients.length} clients
                        </Badge>
                        {trainerClients.length > 0 && (
                            <Tooltip label="View clients">
                                <IconButton
                                    aria-label="View clients"
                                    icon={<Users size={14} />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="blue"
                                    onClick={() => handleViewTrainerClients(info.getValue())}
                                />
                            </Tooltip>
                        )}
                    </HStack>
                );
            },
        }),
        columnHelper.accessor(row => row.isActive, {
            id: 'status',
            header: () => (
                <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
                    Status
                </Text>
            ),
            cell: (info) => (
                <Badge
                    colorScheme={info.getValue() ? 'green' : 'red'}
                    variant="solid"
                    fontSize="xs"
                >
                    {info.getValue() ? 'Active' : 'Inactive'}
                </Badge>
            ),
        }),
        columnHelper.accessor(row => row, {
            id: 'actions',
            header: () => (
                <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
                    Actions
                </Text>
            ),
            cell: (info) => {
                const trainer = info.getValue();
                return (
                    <Tooltip 
                        label={trainer.isActive ? 'Deactivate Trainer' : 'Activate Trainer'}
                    >
                        <IconButton
                            aria-label={trainer.isActive ? 'Deactivate' : 'Activate'}
                            icon={trainer.isActive ? <UserX size={16} /> : <UserCheck size={16} />}
                            size="sm"
                            colorScheme={trainer.isActive ? 'red' : 'green'}
                            variant="ghost"
                            onClick={() => handleStatusToggle(trainer.id, trainer.isActive)}
                        />
                    </Tooltip>
                );
            },
        }),
    ];

    const table = useReactTable({
        data: filteredTrainers,
        columns,
        state: {
            pagination: { pageIndex, pageSize }
        },
        manualPagination: true,
        pageCount: Math.ceil(totalCount / pageSize),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <>
            <Card 
                bg={bgColor} 
                borderRadius="20px"
                boxShadow="0 20px 40px -12px rgba(0,0,0,0.1)"
                border="1px solid"
                borderColor={useColorModeValue("gray.100", "gray.700")}
                overflow="hidden"
                p={{ base: 4, md: 6 }}
            >
                <Flex direction="column" w="100%">
                    {/* Header */}
                    <Flex 
                        justify="space-between" 
                        align={{ base: "start", md: "center" }}
                        direction={{ base: "column", md: "row" }}
                        gap={{ base: 4, md: 0 }}
                        mb="20px"
                    >
                        <Text color={textColor} fontSize={{ base: "lg", md: "22px" }} fontWeight="700">
                            Trainers Management
                        </Text>
                        <Box w={{ base: "full", md: "auto" }}>
                            {/* Mobile: Stack buttons vertically */}
                            <VStack spacing={3} display={{ base: "flex", sm: "none" }} w="full">
                                <Button
                                    leftIcon={<Plus size={16} />}
                                    colorScheme="brand"
                                    onClick={onOpen}
                                    size="md"
                                    w="full"
                                    h="44px"
                                >
                                    Create Trainer
                                </Button>
                                <Button
                                    leftIcon={<UserPlus size={16} />}
                                    colorScheme="green"
                                    variant="outline"
                                    onClick={onSignupLinkModalOpen}
                                    size="md"
                                    w="full"
                                    h="44px"
                                >
                                    Generate Link
                                </Button>
                                <Button
                                    leftIcon={<RefreshCw size={16} />}
                                    variant="outline"
                                    onClick={() => fetchTrainers()}
                                    isLoading={isLoading}
                                    loadingText="Refreshing..."
                                    size="md"
                                    w="full"
                                    h="44px"
                                >
                                    Refresh
                                </Button>
                            </VStack>

                            {/* Tablet & Desktop: Horizontal layout */}
                            <HStack spacing={3} display={{ base: "none", sm: "flex" }} flexWrap="wrap">
                                <Button
                                    leftIcon={<Plus size={16} />}
                                    colorScheme="brand"
                                    onClick={onOpen}
                                    size={{ base: "sm", md: "md" }}
                                    minW="120px"
                                >
                                    <Text display={{ base: "none", md: "block" }}>Create Trainer</Text>
                                    <Text display={{ base: "block", md: "none" }}>Create</Text>
                                </Button>
                                <Button
                                    leftIcon={<UserPlus size={16} />}
                                    colorScheme="green"
                                    variant="outline"
                                    onClick={onSignupLinkModalOpen}
                                    size={{ base: "sm", md: "md" }}
                                    minW="120px"
                                >
                                    <Text display={{ base: "none", md: "block" }}>Generate Link</Text>
                                    <Text display={{ base: "block", md: "none" }}>Link</Text>
                                </Button>
                                <Button
                                    leftIcon={<RefreshCw size={16} />}
                                    variant="outline"
                                    onClick={() => fetchTrainers()}
                                    isLoading={isLoading}
                                    loadingText="Refreshing..."
                                    size={{ base: "sm", md: "md" }}
                                    minW="100px"
                                >
                                    Refresh
                                </Button>
                            </HStack>
                        </Box>
                    </Flex>

                {/* Filters */}
                <VStack spacing={4} align="stretch" mb="20px">
                    <Flex 
                        direction={{ base: "column", md: "row" }}
                        gap={4}
                        align="stretch"
                        w="full"
                    >
                        <InputGroup flex="1" w={{ base: "100%", md: "auto" }}>
                            <InputLeftElement pointerEvents="none">
                                <Search size={16} color="gray" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search trainers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                bg={useColorModeValue('white', 'navy.700')}
                                borderColor={useColorModeValue('gray.200', 'gray.600')}
                                color={textColor}
                                _focus={{ 
                                    borderColor: 'blue.500',
                                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                }}
                                _placeholder={{ 
                                    color: useColorModeValue('gray.400', 'gray.500') 
                                }}
                                fontSize={{ base: "md", md: "md" }}
                                h="44px"
                            />
                        </InputGroup>

                        <Select
                            placeholder="All Status"
                            value={activeFilter}
                            onChange={(e) => {
                                setActiveFilter(e.target.value);
                                setPageIndex(0);
                            }}
                            w={{ base: "100%", md: "200px" }}
                            bg={useColorModeValue('white', 'navy.700')}
                            borderColor={useColorModeValue('gray.200', 'gray.600')}
                            color={textColor}
                            _focus={{ 
                                borderColor: 'blue.500',
                                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                            }}
                            fontSize={{ base: "md", md: "md" }}
                            h="44px"
                        >
                            <option value="all">All Trainers</option>
                            <option value="active">Active Only</option>
                        </Select>
                    </Flex>

                    {/* Mobile Stats */}
                    <HStack spacing={6} justify="center" display={{ base: 'flex', lg: 'none' }}>
                        <Box textAlign="center">
                            <Text fontSize="lg" fontWeight="bold" color="green.500">
                                {trainers.filter(t => t.isActive).length}
                            </Text>
                            <Text fontSize="xs" color={textColorSecondary}>
                                Active
                            </Text>
                        </Box>
                        <Box textAlign="center">
                            <Text fontSize="lg" fontWeight="bold" color="red.500">
                                {trainers.filter(t => !t.isActive).length}
                            </Text>
                            <Text fontSize="xs" color={textColorSecondary}>
                                Inactive
                            </Text>
                        </Box>
                    </HStack>

                    {/* Desktop Stats */}
                    <HStack spacing={4} justify="flex-end" display={{ base: 'none', lg: 'flex' }}>
                        <Box textAlign="center">
                            <Text fontSize="lg" fontWeight="bold" color="green.500">
                                {trainers.filter(t => t.isActive).length}
                            </Text>
                            <Text fontSize="xs" color={textColorSecondary}>
                                Active
                            </Text>
                        </Box>
                        <Box textAlign="center">
                            <Text fontSize="lg" fontWeight="bold" color="red.500">
                                {trainers.filter(t => !t.isActive).length}
                            </Text>
                            <Text fontSize="xs" color={textColorSecondary}>
                                Inactive
                            </Text>
                        </Box>
                    </HStack>
                </VStack>

                {/* Table Content */}
                {isLoading ? (
                    <Flex justify="center" align="center" minH="200px">
                        <Spinner size="xl" />
                    </Flex>
                ) : filteredTrainers.length > 0 ? (
                    <>
                        <TableContainer>
                            <Table variant="simple" color={textColorSecondary} mb="24px" mt="12px">
                                <Thead>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <Tr key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => (
                                                <Th
                                                    key={header.id}
                                                    colSpan={header.colSpan}
                                                    pe="10px"
                                                    borderColor={borderColor}
                                                    cursor="pointer"
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    <Flex
                                                        justifyContent="space-between"
                                                        align="center"
                                                        fontSize={{ sm: '10px', lg: '12px' }}
                                                        color="gray.400"
                                                    >
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                        {{
                                                            asc: '',
                                                            desc: '',
                                                        }[header.column.getIsSorted() as string] ?? null}
                                                    </Flex>
                                                </Th>
                                            ))}
                                        </Tr>
                                    ))}
                                </Thead>
                                <Tbody>
                                    {table.getRowModel().rows.map((row) => {
                                        const trainer = row.original;
                                        return (
                                            <Tr
                                                key={row.id}
                                                bg={!trainer.isActive ? useColorModeValue('red.50', 'red.900') : 'transparent'}
                                                _hover={{ bg: useColorModeValue('gray.50', 'whiteAlpha.100') }}
                                            >
                                                {row.getVisibleCells().map((cell) => (
                                                    <Td
                                                        key={cell.id}
                                                        fontSize={{ sm: '14px' }}
                                                        minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                        borderColor="transparent"
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </Td>
                                                ))}
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </TableContainer>

                        {/* Custom Pagination */}
                        <CustomPagination 
                            totalResults={totalCount} 
                            pageSize={pageSize} 
                            onPageChange={(newPageIndex: number) => {
                                setPageIndex(newPageIndex);
                            }}
                        />
                    </>
                ) : (
                    <Flex justify="center" align="center" minH="200px" px={4}>
                        <VStack spacing="20px" textAlign="center">
                            <Text color={textColor} fontSize={{ base: "md", md: "18px" }}>
                                No trainers found
                            </Text>
                            <Button
                                leftIcon={<Plus size={16} />}
                                colorScheme="brand"
                                onClick={onOpen}
                                size={{ base: "md", md: "lg" }}
                                w={{ base: "full", md: "auto" }}
                                maxW="250px"
                            >
                                Create First Trainer
                            </Button>
                        </VStack>
                    </Flex>
                )}
                </Flex>
            </Card>

            {/* Modals */}
            <CreateTrainerModal
                isOpen={isOpen}
                onClose={onClose}
                onTrainerCreated={fetchTrainers}
            />

            <GenerateSignupLinkModal
                isOpen={isSignupLinkModalOpen}
                onClose={onSignupLinkModalClose}
            />

            <ViewTrainerClientsModal
                isOpen={isClientsModalOpen}
                onClose={onClientsModalClose}
                trainer={selectedTrainerForModal}
            />
        </>
    );
};

export default TrainersTable;
